import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Layout, Zap, Search, Award, Quote, Rocket } from 'lucide-react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Link } from 'react-router-dom'; // For internal navigation
import Navbar from '../../components/Navbar'; // Adjust path or remove if not available

const WebDevelopmentPage = () => {
  // Hero Section: Full-screen with carousel and particle animations
  const HeroSection = () => {
    const screens = [
      { src: 'https://i.imgur.com/bSzpbUi.png', caption: 'PlantSpy' },
      { src: 'https://i.imgur.com/BjLtcPB.png', caption: 'Demo Site' },
      { src: 'https://i.imgur.com/0RX4yBN.png', caption: 'This Website' },
    ];

    return (
      <section
        id="hero"
        className="min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-tr from-pink-900 via-purple-800 to-indigo-900 relative overflow-hidden"
      >
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-pink-400 rounded-full"
              initial={{ x: Math.random() * 100 + '%', y: Math.random() * 100 + '%' }}
              animate={{ y: [0, -1000], opacity: [0, 1, 0] }}
              transition={{ duration: Math.random() * 5 + 5, repeat: Infinity, delay: Math.random() * 2, ease: 'easeInOut' }}
            />
          ))}
        </div>
        <div className="relative z-10 max-w-6xl w-full flex flex-col md:flex-row items-center gap-8">
          <motion.div
            className="md:w-1/2 text-center md:text-left"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-500 mb-6">
              Cutting-Edge Websites
            </h1>
            <motion.p
              className="text-xl sm:text-2xl text-pink-200 mb-8"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8, ease: 'easeInOut' }}
            >
              Experience the future of web design.
            </motion.p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.15, rotate: 5, boxShadow: '0 0 15px rgba(236, 72, 153, 0.7)' }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 sm:px-8 sm:py-4 bg-purple-600 text-white rounded-full text-lg sm:text-xl font-semibold shadow-lg transition-colors duration-300 hover:bg-purple-700"
              >
                Contact Us
              </motion.button>
            </Link>
          </motion.div>
          <motion.div
            className="md:w-1/2"
            initial={{ opacity: 0, rotate: -10 }}
            animate={{ opacity: 1, rotate: 0 }}
            transition={{ duration: 1.5, ease: 'easeInOut' }}
          >
            <Carousel showArrows infiniteLoop autoPlay interval={4000} className="max-w-sm sm:max-w-md mx-auto">
              {screens.map((screen, idx) => (
                <div key={idx}>
                  <img src={screen.src} alt={screen.caption} className="rounded-lg shadow-2xl" />
                  <p className="legend text-white">{screen.caption}</p>
                </div>
              ))}
            </Carousel>
          </motion.div>
        </div>
        <svg className="absolute bottom-0 w-full h-24 text-gray-900" viewBox="0 0 1440 100" preserveAspectRatio="none">
          <path fill="currentColor" d="M0,50 Q360,150 720,50 T1440,50 V100 H0 Z" />
        </svg>
      </section>
    );
  };

  // Portfolio Section: Updated with PlantSpy, Demo Site, This Website
  const PortfolioSection = () => {
    const projects = [
      {
        title: 'PlantSpy',
        desc: 'Elevate plant care with PlantSpy – crafted by our skilled developers. Ready to grow your app idea with us?',
        img: 'https://i.imgur.com/bSzpbUi.png',
        liveUrl: 'https://plantspy.aiempower.ca/',
      },
      {
        title: 'Demo Site',
        desc: 'A sleek demo showcasing our design capabilities – built to impress and inspire.',
        img: 'https://i.imgur.com/BjLtcPB.png',
        liveUrl: 'https://tech.aiempower.ca/',
      },
      {
        title: 'This Website',
        desc: 'The site you’re exploring now – a testament to our innovative web development expertise.',
        img: 'https://i.imgur.com/0RX4yBN.png',
        liveUrl: '/contact',
      },
    ];

    return (
      <section id="portfolio" className="py-16 px-4 bg-[url('/circuit-bg.png')] bg-cover bg-center relative overflow-hidden">
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-pink-300 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, ease: 'easeInOut' }}
        >
          Our Masterpieces
        </motion.h2>
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {projects.map((project, idx) => (
            <motion.div
              key={idx}
              className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-lg shadow-xl overflow-hidden"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.3, duration: 0.8, ease: 'easeInOut' }}
              whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(255, 105, 180, 0.7)' }}
            >
              <img src={project.img} alt={project.title} className="w-full h-48 object-cover" />
              <div className="p-4 text-center">
                <h3 className="text-xl font-semibold text-white mb-2">{project.title}</h3>
                <p className="text-pink-200 text-sm mb-4">{project.desc}</p>
                <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                  <motion.button
                    whileHover={{ scale: 1.1, boxShadow: '0 0 10px rgba(236, 72, 153, 0.7)' }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm font-semibold transition-colors duration-300 hover:bg-pink-600"
                  >
                    Visit Live Site
                  </motion.button>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    );
  };

  // Features Section: Static cards
  const FeaturesSection = () => {
    const features = [
      { title: 'Adaptive', desc: 'Fits all screens perfectly.', icon: <Layout className="w-6 h-6 text-pink-400" /> },
      { title: 'Lightning', desc: 'Super fast load times.', icon: <Zap className="w-6 h-6 text-purple-400" /> },
      { title: 'Visible', desc: 'SEO optimized for reach.', icon: <Search className="w-6 h-6 text-indigo-400" /> },
    ];

    return (
      <section id="features" className="py-16 px-4 bg-gradient-to-b from-gray-900 to-purple-950 relative overflow-hidden">
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-purple-300 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, ease: 'easeInOut' }}
        >
          What We Offer
        </motion.h2>
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              className="bg-gradient-to-t from-purple-900/50 to-indigo-900/50 p-6 rounded-xl border border-pink-500/30 text-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: idx * 0.2, ease: 'easeInOut' }}
              whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(255, 105, 180, 0.6)' }}
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                {feature.icon}
                <h3 className="text-lg sm:text-xl font-semibold text-white">{feature.title}</h3>
              </div>
              <p className="text-pink-200 text-sm sm:text-base">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    );
  };

  // Benefits Section: Wavy timeline on desktop, cards on phone
  const BenefitsSection = () => {
    const benefits = [
      { title: 'Reach', desc: 'Attract a wider audience with stunning visuals.' },
      { title: 'Engage', desc: 'Keep users captivated with seamless UX.' },
      { title: 'Convert', desc: 'Turn visitors into loyal customers.' },
    ];

    return (
      <section id="benefits" className="py-16 px-4 bg-[radial-gradient(circle_at_center,rgba(147,51,234,0.8),rgba(236,72,153,0.8),rgba(0,0,0,0.9))] relative overflow-hidden">
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-pink-300 mb-12 flex items-center justify-center gap-4"
          initial={{ y: -50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          <Award className="w-8 h-8 text-purple-400" />
          Your Gains
        </motion.h2>
        {/* Desktop: Wavy Timeline */}
        <div className="hidden sm:block max-w-2xl mx-auto relative">
          <svg className="absolute left-1/2 w-2 h-full" preserveAspectRatio="none" viewBox="0 0 10 100">
            <motion.path
              d="M5 0 Q7 25 5 50 Q3 75 5 100"
              stroke="purple"
              strokeWidth="4"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              transition={{ duration: 2, ease: 'easeInOut' }}
            />
          </svg>
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              className={`flex items-center mb-12 ${idx % 2 === 0 ? 'flex-row-reverse text-right' : 'text-left'}`}
              initial={{ opacity: 0, x: idx % 2 === 0 ? 100 : -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: idx * 0.3, ease: 'easeInOut' }}
            >
              <div className="w-1/2 p-4 relative z-10">
                <h3 className="text-2xl font-semibold text-white bg-gradient-to-r from-purple-500/30 to-pink-500/30 p-2 rounded-lg">
                  {benefit.title}
                </h3>
                <p className="text-base text-pink-200 mt-2">{benefit.desc}</p>
              </div>
              <motion.div
                className="w-8 h-8 bg-pink-500 rounded-full absolute left-1/2 -translate-x-1/2"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1, rotate: 360 }}
                transition={{ duration: 0.8, delay: idx * 0.3, ease: 'easeInOut' }}
                style={{ top: `${idx * 33 + 10}%` }}
              />
            </motion.div>
          ))}
          <div className="absolute inset-0 bg-[url('/stars.png')] bg-cover opacity-10 pointer-events-none" />
        </div>
        {/* Phone: Cards */}
        <div className="block sm:hidden max-w-md mx-auto space-y-6">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              className="bg-gradient-to-t from-purple-900/50 to-indigo-900/50 p-6 rounded-xl border border-pink-500/30 text-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: idx * 0.2, ease: 'easeInOut' }}
              whileHover={{ scale: 1.05 }}
            >
              <h3 className="text-xl font-semibold text-white">{benefit.title}</h3>
              <p className="text-sm text-pink-200 mt-2">{benefit.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>
    );
  };

  // Testimonials Section: Single static card
  const TestimonialsSection = () => {
    return (
      <section id="testimonials" className="py-16 px-4 bg-gradient-to-r from-indigo-900 to-purple-900 relative overflow-hidden">
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-purple-300 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          Voices of Success
        </motion.h2>
        <motion.div
          className="max-w-2xl mx-auto bg-purple-800/70 p-6 sm:p-8 rounded-lg shadow-lg text-center"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          whileHover={{ scale: 1.03, boxShadow: '0 0 15px rgba(147, 51, 234, 0.6)' }}
        >
          <Quote className="w-8 h-8 text-pink-400 mx-auto mb-4" />
          <p className="text-white text-lg sm:text-xl italic">"If you’re looking for a team that truly understands how to bridge AI with practical business needs, AI Empower is the partner you need."</p>
          <p className="text-pink-200 mt-4 font-semibold text-sm sm:text-base">- Memory Bloom</p>
        </motion.div>
      </section>
    );
  };

  // CTA Section: Video background with bouncing CTA
  const CTASection = () => {
    return (
      <section id="cta" className="py-16 px-4 relative overflow-hidden">
        <video
          autoPlay
          loop
          muted
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          src="/bg-video.mp4" // Replace with actual video URL
        />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8"
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            Launch Your Dream Site
          </motion.h2>
          <motion.p
            className="text-xl sm:text-2xl text-purple-200 mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, ease: 'easeInOut' }}
          >
            Let’s make it happen today!
          </motion.p>
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.2, rotate: [0, 5, -5, 0], boxShadow: '0 0 20px rgba(236, 72, 153, 0.7)' }}
              whileTap={{ scale: 0.95 }}
              className="px-8 sm:px-10 py-3 sm:py-4 bg-pink-600 text-white rounded-full text-lg sm:text-xl font-bold shadow-xl transition-colors duration-300 hover:bg-pink-700"
            >
              Contact Us Now
            </motion.button>
          </Link>
        </div>
      </section>
    );
  };

  return (
    <div className="bg-gray-900 text-white overflow-x-hidden">
      
      <HeroSection />
      <PortfolioSection />
      <FeaturesSection />
      <BenefitsSection />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
};

export default WebDevelopmentPage;